// src/JanusAudioTest.tsx
import React, { useEffect, useRef, useState } from 'react';

// Let TypeScript know about the global Janus
declare global {
  interface Window {
    Janus: any;
  }
}

const VOLUME_THRESHOLD = 0.01; // Adjust to make "speaking" detection more/less sensitive

/**
 * A simple audio-only Janus "VideoRoom" test component.
 * 1) Lets you enter a display name and room ID.
 * 2) Joins the room as an audio-only publisher.
 * 3) Detects remote audio volume for a basic "speaking" indicator.
 */
const JanusAudioTest: React.FC = () => {
  const [displayName, setDisplayName] = useState('');
  const [roomId, setRoomId] = useState(1234);
  const [joined, setJoined] = useState(false);

  const localAudioRef = useRef<HTMLAudioElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);

  // Janus session/plugin references
  const [janus, setJanus] = useState<any>(null);
  const [pluginHandle, setPluginHandle] = useState<any>(null);

  console.log('janus', janus);
  console.log('pluginHandle', pluginHandle);

  // For remote speaking indicator
  const [remoteSpeaking, setRemoteSpeaking] = useState(false);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!joined) return;
    if (!displayName.trim() || !roomId) return;

    const Janus = window.Janus;
    let attachedHandle: any = null;
    let janusSession: any = null;

    // Check mic permission first
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        // Stop the probe so Janus can properly acquire the mic
        stream.getTracks().forEach((track) => track.stop());
        initJanus();
      })
      .catch((err) => {
        console.error('Mic permission denied or error:', err);
        alert('Microphone access was denied. Please enable your mic and refresh.');
      });

    function initJanus() {
      Janus.init({
        debug: 'all',
        callback: createSession,
      });
    }

    function createSession() {
      janusSession = new Janus({
        server: 'wss://janus.umbre.ai', // Replace with your Janus server
        iceServers: [
          {
            urls: 'stun:50.18.32.161:3478',
          },
          {
            urls: 'turn:50.18.32.161:3478',
            username: 'umbre',
            credential: 'turn',
          },
        ],
        success: () => {
          janusSession.attach({
            plugin: 'janus.plugin.videoroom',
            success: (handle: any) => {
              attachedHandle = handle;
              setPluginHandle(handle);

              // Join the room as a publisher
              handle.send({
                message: {
                  request: 'join',
                  room: roomId,
                  ptype: 'publisher',
                  display: displayName,
                },
              });

              // Publish audio-only
              handle.createOffer({
                media: { audio: true, video: false },
                success: (jsep: any) => {
                  handle.send({
                    message: { request: 'publish' },
                    jsep,
                  });
                },
                error: (error: any) => {
                  console.error('createOffer error:', error);
                  alert('Error creating offer. Check console for details.');
                },
              });
            },
            error: (error: any) => {
              console.error('Error attaching plugin:', error);
            },
            onmessage: (jsep: any) => {
              // Handle incoming JSEP (remote feed, etc.)
              if (jsep) {
                attachedHandle?.handleRemoteJsep({ jsep });
              }
            },
            onlocalstream: (stream: MediaStream) => {
              if (localAudioRef.current) {
                localAudioRef.current.srcObject = stream;
              }
            },
            onremotestream: (stream: MediaStream) => {
              // Play the remote stream
              if (remoteAudioRef.current) {
                remoteAudioRef.current.srcObject = stream;
              }
              // Set up volume analysis for remote stream
              setupRemoteVolumeAnalysis(stream);
            },
          });
        },
        error: (err: any) => {
          console.error('Janus session error:', err);
          alert('Could not create Janus session. Check console for details.');
        },
      });
      setJanus(janusSession);
    }

    // Cleanup
    return () => {
      if (attachedHandle) {
        attachedHandle.detach();
      }
      if (janusSession) {
        janusSession.destroy();
      }
      stopVolumeAnalysis();
    };
  }, [joined, displayName, roomId]);

  // Starts or updates an analyser node for the remote stream
  const setupRemoteVolumeAnalysis = (stream: MediaStream) => {
    stopVolumeAnalysis(); // in case there's an old one

    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);
    analyserRef.current = analyser;

    detectVolume();
  };

  // Loop to read analyser data and decide if remote is speaking
  const detectVolume = () => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.fftSize);
    analyserRef.current.getByteTimeDomainData(dataArray);

    // Compute a rough RMS to decide if speaking
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const val = (dataArray[i] - 128) / 128; // center around 0
      sum += val * val;
    }
    const rms = Math.sqrt(sum / dataArray.length);

    // If above a threshold, consider that "speaking"
    setRemoteSpeaking(rms > VOLUME_THRESHOLD);

    // Continue loop
    animationIdRef.current = requestAnimationFrame(detectVolume);
  };

  // Stop any ongoing volume analysis
  const stopVolumeAnalysis = () => {
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
    }
    analyserRef.current = null;
    setRemoteSpeaking(false);
  };

  const handleJoin = () => {
    if (!displayName.trim()) {
      alert('Please enter a name.');
      return;
    }
    setJoined(true);
  };

  return (
    <div style={{ padding: '1rem' }}>
      {!joined && (
        <div>
          <h2>Join Janus Audio Room</h2>
          <div style={{ marginBottom: '0.5rem' }}>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your name"
              style={{ marginRight: '0.5rem' }}
            />
            <input
              type="number"
              value={roomId}
              onChange={(e) => setRoomId(Number(e.target.value))}
              placeholder="Room ID"
              style={{ marginRight: '0.5rem' }}
            />
            <button onClick={handleJoin}>Join Room</button>
          </div>
          <p>Open a second tab, enter a different name &amp; same room ID to test audio.</p>
        </div>
      )}

      {joined && (
        <div>
          <h3>
            Joined room {roomId} as &quot;{displayName}&quot;
          </h3>
          <p>Open another tab with a different name, same room. Speak and watch the indicator below!</p>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', alignItems: 'center' }}>
            {/* Local mic feed (muted so you don't hear yourself) */}
            <div style={{ textAlign: 'center' }}>
              <strong>Local Audio</strong>
              <audio ref={localAudioRef} autoPlay muted controls style={{ display: 'block' }} />
            </div>

            {/* Remote feed + speaking indicator */}
            <div style={{ textAlign: 'center' }}>
              <strong>Remote Audio</strong>
              <audio ref={remoteAudioRef} autoPlay controls style={{ display: 'block' }} />
              <div style={{ marginTop: '0.5rem' }}>{remoteSpeaking ? '🔊 Speaking' : '🔇 Silent'}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JanusAudioTest;
